import { memo, useEffect, useState } from "react";
import { Box, Modal, IconButton } from "@mui/material";

import Close from "@zenith/assets/icons/Close";
import {
  ButtonComponent,
  PinInputComponent,
  PinRulesComponent,
} from "@zenith/components";
import { PIN_MISMATCH_MESSAGE } from "@zenith/data/constants";

const PASSCODE_LENGTH = 6;
const EMPTY_PIN = new Array(PASSCODE_LENGTH).fill("");

const isSequential = (digits: string): boolean => {
  const asc = Array.from({ length: PASSCODE_LENGTH }, (_, i) =>
    String((Number(digits[0]) + i) % 10)
  ).join("");
  const desc = Array.from({ length: PASSCODE_LENGTH }, (_, i) =>
    String((Number(digits[0]) - i + 10) % 10)
  ).join("");
  return digits === asc || digits === desc;
};

const isRepeated = (digits: string): boolean =>
  digits.split("").every((d) => d === digits[0]);

export interface CreatePasscodeModalProps {
  showModal: boolean;
  isSaving?: boolean;
  onClose: () => void;
  onBack: () => void;
  onSubmit: (passcode: string) => void;
}

const CreatePasscodeModal = memo(function CreatePasscodeModal(
  props: CreatePasscodeModalProps
) {
  const { showModal, isSaving, onClose, onBack, onSubmit } = props;
  const [passcode, setPasscode] = useState<Array<number | string>>(EMPTY_PIN);
  const [confirmPasscode, setConfirmPasscode] =
    useState<Array<number | string>>(EMPTY_PIN);
  const [error, setError] = useState("");

  const resetState = () => {
    setPasscode(EMPTY_PIN);
    setConfirmPasscode(EMPTY_PIN);
    setError("");
  };

  // The flow can finish from the success screen without this step ever being
  // closed, so opening it again has to start blank - a passcode from the last
  // member must not sit in these boxes.
  useEffect(() => {
    if (showModal) {
      setPasscode(EMPTY_PIN);
      setConfirmPasscode(EMPTY_PIN);
      setError("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleBack = () => {
    resetState();
    onBack();
  };

  const setDigit =
    (
      setter: (v: Array<number | string>) => void,
      current: Array<number | string>
    ) =>
    (entry: number | string, index: number) => {
      const next = [...current];
      next[index] = entry;
      setter(next);
      // A message from a failed save belongs to the values that were sent.
      if (error) setError("");
    };

  const joined = (pin: Array<number | string>) => pin.join("");

  const code = joined(passcode);
  const confirm = joined(confirmPasscode);
  const passcodeComplete = code.length === PASSCODE_LENGTH;
  const confirmComplete = confirm.length === PASSCODE_LENGTH;

  // The rule has to be read here, not only on submit: a passcode of 111111 is
  // six digits long, so a submit-only check left "Add" active and made the
  // button the thing that told you the rules were broken.
  const ruleProblem = !passcodeComplete
    ? ""
    : isRepeated(code)
    ? "Your passcode cannot be repeated numbers only."
    : isSequential(code)
    ? "Your passcode cannot be sequential numbers only."
    : "";

  // A confirmation that is finished and different is wrong right away - making
  // the user press Add first meant the mismatch only surfaced after submitting.
  const mismatch = confirmComplete && code !== confirm;

  const canSubmit =
    passcodeComplete &&
    confirmComplete &&
    !ruleProblem &&
    !mismatch &&
    !isSaving;

  const handleSubmit = () => {
    if (!canSubmit) return;
    setError("");
    onSubmit(code);
  };

  return (
    <Modal open={showModal} onClose={handleClose} className="overflow-visible">
      <Box className="absolute left-1/2 top-1/2 z-0 flex w-[95%] max-w-[550px] -translate-x-1/2 -translate-y-1/2 transform flex-col rounded-xl bg-white p-8 outline-none">
        <div className="mb-4 flex justify-end">
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </div>

        <h2 className="mb-2 text-2xl font-semibold text-neutral-900">
          Create Passcode
        </h2>
        <p className="mb-6 text-sm text-neutral-600">
          Enter passcode team member will use to access account
        </p>

        <div className="mb-5">
          <PinInputComponent
            id="new-passcode"
            label="Create six (6) digit Passcode"
            labelClassName=""
            pin={passcode}
            onPinChanged={setDigit(setPasscode, passcode)}
            pinLength={PASSCODE_LENGTH}
            pinContainerClass="gap-2"
            inputClassName="w-11"
            masked
            hasError={!!ruleProblem}
            validationMessage={ruleProblem}
            onBlur={() => undefined}
          />
        </div>

        <div className="mb-5">
          <PinInputComponent
            id="confirm-passcode"
            label="Confirm six (6) digit Passcode"
            labelClassName=""
            pin={confirmPasscode}
            onPinChanged={setDigit(setConfirmPasscode, confirmPasscode)}
            pinLength={PASSCODE_LENGTH}
            pinContainerClass="gap-2"
            inputClassName="w-11"
            masked
            hasError={mismatch || !!error}
            validationMessage={mismatch ? PIN_MISMATCH_MESSAGE : error}
            onBlur={() => undefined}
          />
        </div>

        <div className="mb-6 rounded-lg bg-[#5656CA14] p-4">
          {/* The rules react to what has been typed, the way the sign-up flow
              does: as soon as the passcode is complete each rule turns green
              or red. A static list told nobody which rule they had broken. */}
          <PinRulesComponent
            className=""
            isRepeated={passcodeComplete && isRepeated(code)}
            isSequential={passcodeComplete && isSequential(code)}
            necessaryPinLength={PASSCODE_LENGTH}
            pinLength={code.length}
          />
        </div>

        <div className="flex gap-4">
          <ButtonComponent
            id="create-passcode-back"
            type="secondary"
            size="large"
            label="Back"
            className="flex-1"
            onClick={handleBack}
          />
          <ButtonComponent
            id="create-passcode-add"
            type="primary"
            size="large"
            label="Add"
            className="flex-1"
            disabled={!canSubmit}
            isLoading={isSaving}
            onClick={handleSubmit}
          />
        </div>
      </Box>
    </Modal>
  );
});

export default CreatePasscodeModal;
